import { useEffect, useRef, useState } from "react";
import { api, formatError, formatNZD, imageUrl, timeAgo } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Camera, Trash2, ImageUp, ExternalLink, Wallet, Zap, Store as StoreIcon, Printer, Phone, RotateCw } from "lucide-react";
import { Link } from "react-router-dom";
import WalletTopupDialog from "@/components/app/WalletTopupDialog";
import InsightsCard from "@/components/app/InsightsCard";
import PlanCard, { nzDate } from "@/components/app/PlanCard";

const publishLabel = (w) => {
  if (!w.can_post) return "Join the Fiver Deal to post adverts";
  if (w.monthly_credits > 0) return `Publish · use 1 monthly credit (${w.monthly_credits} left)`;
  if (w.listing_credits > 0) return `Publish · use 1 bonus credit (${w.listing_credits} left)`;
  if (w.balance >= w.listing_price) return `Publish · $${w.listing_price.toFixed(2)} from wallet`;
  return `Publish · pay $${w.listing_price.toFixed(2)} NZD via Stripe`;
};

function FollowerCountLoader() {
  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/stores/me/followers");
        const el = document.querySelector('[data-testid="follower-count"]');
        if (el) el.textContent = String(data.count);
      } catch (e) { /* ignore */ }
    })();
  }, []);
  return null;
}

function ShopUpload({ label, field, currentPath, onDone, testid }) {
  const [busy, setBusy] = useState(false);
  const [path, setPath] = useState(currentPath || "");
  useEffect(() => setPath(currentPath || ""), [currentPath]);
  const pick = async (f) => {
    if (!f) return;
    setBusy(true);
    try {
      const fd = new FormData(); fd.append("file", f);
      const up = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      await api.put("/stores/me/shopfront", { [field]: up.data.path });
      setPath(up.data.path);
      toast.success(`${label} updated`);
      onDone && onDone();
    } catch (e) { toast.error(formatError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  return (
    <label className="border border-dashed border-emerald-300 rounded-xl p-2 flex flex-col items-center justify-center cursor-pointer hover:bg-emerald-50/60 aspect-square" data-testid={testid}>
      {path ? <img src={`${process.env.REACT_APP_BACKEND_URL}/api/files/${path}`} alt={label} className="w-full h-full object-cover rounded-lg" /> : <span className="text-xs text-emerald-900 font-semibold">{busy ? "Uploading…" : `+ ${label}`}</span>}
      <input type="file" accept="image/*" className="hidden" onChange={(e)=>pick(e.target.files?.[0])} />
    </label>
  );
}

export default function StoreDashboard() {
  const { user, refresh } = useAuth();
  const [mine, setMine] = useState([]);
  const [wallet, setWallet] = useState({ balance: 0, listing_credits: 0, listing_price: 2, boost_price: 1, fiver_price: 5, fiver_credits: 28 });
  const [topupOpen, setTopupOpen] = useState(false);
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState("");
  const [uploadingPath, setUploadingPath] = useState("");
  const [form, setForm] = useState({ product_name: "", description: "", was_price: "", now_price: "", category: "" });
  const [busy, setBusy] = useState(false);
  const inputRef = useRef(null);

  const loadWallet = async () => {
    try { const { data } = await api.get("/wallet"); setWallet(data); } catch (e) { /* ignore */ }
  };
  const load = async () => {
    try {
      const { data } = await api.get("/markdowns/mine");
      setMine(data);
    } catch (e) { /* ignore */ }
  };

  useEffect(() => { load(); loadWallet(); }, []);

  const pickFile = (f) => {
    if (!f) return;
    setFile(f);
    setPreview(URL.createObjectURL(f));
    setUploadingPath("");
  };

  const uploadImage = async () => {
    if (!file) return null;
    const fd = new FormData();
    fd.append("file", file);
    const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
    setUploadingPath(data.path);
    return data.path;
  };

  const percentOff = (() => {
    const was = Number(form.was_price) || 0, now = Number(form.now_price) || 0;
    return was > 0 && now < was ? Math.round((was - now) / was * 100) : 0;
  })();

  const submit = async (e) => {
    e.preventDefault();
    if (!file && !uploadingPath) { toast.error("Please add a photo of the markdown"); return; }
    if (Number(form.now_price) >= Number(form.was_price)) { toast.error("Now price must be less than the was price"); return; }
    setBusy(true);
    try {
      const path = uploadingPath || await uploadImage();
      const { data } = await api.post("/markdowns", {
        ...form,
        was_price: Number(form.was_price),
        now_price: Number(form.now_price),
        image_path: path,
        origin_url: window.location.origin,
      });
      if (data?.checkout_url) {
        toast.message("Redirecting to Stripe for the $2 NZD listing fee…");
        window.location.href = data.checkout_url;
        return;
      }
      toast.success(data?.message || "Markdown published live on Blurblive.nz 🎉");
      setForm({ product_name: "", description: "", was_price: "", now_price: "", category: "" });
      setFile(null); setPreview(""); setUploadingPath("");
      if (inputRef.current) inputRef.current.value = "";
      load(); loadWallet(); refresh();
    } catch (err) {
      toast.error(formatError(err.response?.data?.detail) || err.message);
    } finally {
      setBusy(false);
    }
  };

  const renew = async (id) => {
    if (!window.confirm("Renew this advert for another 30 days? Uses 1 credit.")) return;
    try {
      const { data } = await api.post(`/markdowns/${id}/renew`);
      toast.success(`Renewed until ${nzDate(data.valid_until)}`);
      load(); loadWallet();
    } catch (err) { toast.error(formatError(err.response?.data?.detail)); }
  };

  const remove = async (id) => {
    if (!window.confirm("Remove this markdown?")) return;
    try {
      await api.delete(`/markdowns/${id}`);
      toast.success("Removed");
      load();
    } catch (err) { toast.error(formatError(err.response?.data?.detail)); }
  };

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 space-y-8">
      {!user?.approved && user?.role !== "admin" && (
        <div className="rounded-xl border border-amber-300 bg-amber-50 text-amber-900 px-4 py-3 text-sm" data-testid="pending-approval-banner">
          <b>Awaiting approval.</b> Your store account is under review — you&apos;ll be able to publish markdowns once approved.
        </div>
      )}

      <div className="grid lg:grid-cols-5 gap-8">
        <Card className="lg:col-span-3 border-slate-200">
          <CardHeader>
            <CardTitle className="font-display text-2xl flex items-center gap-2"><Camera className="w-6 h-6 text-emerald-800" />Snap & list a markdown</CardTitle>
            <CardDescription>Point your phone camera at the marked-down product, then fill in the details.</CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={submit} className="space-y-4">
              <div>
                <Label className="mb-2 block">Product photo</Label>
                {preview ? (
                  <div className="relative rounded-xl overflow-hidden border border-slate-200">
                    <img src={preview} alt="preview" className="w-full aspect-[4/3] object-cover" />
                    <button type="button" onClick={()=>{ setFile(null); setPreview(""); setUploadingPath(""); if (inputRef.current) inputRef.current.value=""; }} className="absolute top-2 right-2 bg-white/95 rounded-full px-3 py-1 text-xs font-semibold shadow" data-testid="clear-photo-btn">Retake</button>
                  </div>
                ) : (
                  <label htmlFor="photo-input" className="flex flex-col items-center justify-center aspect-[4/3] rounded-xl border-2 border-dashed border-emerald-300 bg-emerald-50/40 hover:bg-emerald-50 cursor-pointer text-emerald-800" data-testid="photo-dropzone">
                    <ImageUp className="w-10 h-10 mb-2" />
                    <span className="font-semibold">Tap to snap or upload</span>
                    <span className="text-xs text-emerald-700/70 mt-1">JPG / PNG / WEBP up to 8MB</span>
                  </label>
                )}
                <input ref={inputRef} id="photo-input" data-testid="photo-input" type="file" accept="image/*" capture="environment" className="hidden" onChange={(e)=>pickFile(e.target.files?.[0])} />
              </div>

              <div>
                <Label>Product name</Label>
                <Input data-testid="md-product-name" value={form.product_name} onChange={(e)=>setForm({...form, product_name: e.target.value})} required placeholder="e.g. Organic Waikato Milk 2L" />
              </div>
              <div>
                <Label>Description (optional)</Label>
                <Textarea data-testid="md-description" value={form.description} onChange={(e)=>setForm({...form, description: e.target.value})} rows={2} placeholder="Best-before today, half price…" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>Was price (NZD)</Label>
                  <Input data-testid="md-was-price" type="number" step="0.01" min="0" value={form.was_price} onChange={(e)=>setForm({...form, was_price: e.target.value})} required />
                </div>
                <div>
                  <Label>Now price (NZD)</Label>
                  <Input data-testid="md-now-price" type="number" step="0.01" min="0" value={form.now_price} onChange={(e)=>setForm({...form, now_price: e.target.value})} required />
                </div>
              </div>
              <div>
                <Label>Category (optional)</Label>
                <Input data-testid="md-category" value={form.category} onChange={(e)=>setForm({...form, category: e.target.value})} placeholder="e.g. Bakery, Meat, Produce" />
              </div>

              <p className="text-xs text-slate-500" data-testid="listing-duration-note">Adverts stay up for 30 days or until you remove them. Renew any time for 1 credit.</p>

              {percentOff > 0 && (
                <div className="rounded-xl bg-emerald-50 border border-emerald-200 px-4 py-3 flex items-center justify-between" data-testid="preview-savings">
                  <span className="text-sm text-emerald-900">Shoppers will see</span>
                  <span className="font-black text-emerald-900">{percentOff}% OFF · save {formatNZD((form.was_price - form.now_price) || 0)}</span>
                </div>
              )}

              <Button type="submit" disabled={busy || !wallet.can_post || (!user?.approved && user?.role !== "admin")} className="w-full h-11 bg-emerald-800 hover:bg-emerald-900 text-white" data-testid="publish-btn">
                {busy ? "Preparing…" : publishLabel(wallet)}
              </Button>
              <p className="text-xs text-slate-500 text-center">Extra adverts beyond your 28 monthly credits are $2 each from your wallet. Secure payments via Stripe.</p>
            </form>
          </CardContent>
        </Card>

        <div className="lg:col-span-2 space-y-4">
          {user?.role !== "admin" && <PlanCard plan={wallet} onChange={() => { loadWallet(); refresh(); }} />}
          <Card className="border-emerald-200 bg-emerald-50/40" data-testid="wallet-card">
            <CardContent className="py-4 space-y-3">
              <div className="flex items-center gap-4">
                <div className="w-11 h-11 rounded-xl bg-emerald-800 text-white grid place-items-center"><Wallet className="w-5 h-5" /></div>
                <div className="flex-1">
                  <div className="text-xs font-black uppercase tracking-widest text-emerald-800">Wallet · Credits</div>
                  <div className="font-display font-black text-xl text-emerald-900" data-testid="wallet-balance">${wallet.balance.toFixed(2)} <span className="text-slate-500 text-sm font-semibold">NZD</span> · <span className="text-amber-600" data-testid="fiver-credits">{wallet.listing_credits} bonus credits</span></div>
                  <div className="text-[10px] text-emerald-700">Wallet pays $2 per extra advert. Bonus credits never expire.</div>
                </div>
                <Button size="sm" variant="outline" onClick={async ()=>{
                try {
                  const { data } = await api.post("/payments/refresh");
                  toast.success(`Wallet refreshed · ${data.updated} payment(s) reconciled · balance $${data.balance.toFixed(2)}`);
                  loadWallet(); refresh(); load();
                } catch (e) { toast.error(formatError(e.response?.data?.detail) || "Refresh failed"); }
              }} data-testid="refresh-wallet-btn" className="border-emerald-300 text-emerald-900 hover:bg-emerald-100">Refresh</Button>
              <Button size="sm" variant="outline" onClick={()=>setTopupOpen(true)} data-testid="topup-open" className="border-emerald-300 text-emerald-900 hover:bg-emerald-100">Top up</Button>
              </div>
            </CardContent>
          </Card>

          <Card className="border-slate-200">
            <CardHeader className="pb-3">
              <CardTitle className="font-display text-xl">Your live markdowns</CardTitle>
              <CardDescription>{mine.length} active {user?.slug && <Link className="text-emerald-800 underline underline-offset-4 inline-flex items-center gap-1" to={`/store/${user.slug}`} data-testid="view-public-page">· view public page <ExternalLink className="w-3 h-3" /></Link>}</CardDescription>
            </CardHeader>
            <CardContent className="space-y-3" data-testid="my-markdowns-list">
              {mine.length === 0 && <div className="text-sm text-slate-500">No markdowns yet.</div>}
              {mine.map(m => (
                <div key={m.id} className="flex gap-3 items-center border border-slate-200 rounded-xl p-2" data-testid={`mine-${m.id}`}>
                  <img src={imageUrl(m.image_path)} alt={m.product_name} className="w-16 h-16 object-cover rounded-lg bg-slate-100" />
                  <div className="flex-1 min-w-0">
                    <div className="font-semibold text-sm truncate">{m.product_name}</div>
                    <div className="text-xs text-slate-500">{formatNZD(m.now_price)} <span className="strike ml-1">{formatNZD(m.was_price)}</span> · {timeAgo(m.created_at)}{m.redemptions ? ` · ${m.redemptions} redeemed` : ""}</div>
                    <div className="text-[11px]" data-testid={`mine-status-${m.id}`}>{m.status === "paused_plan" ? <span className="text-slate-500">Hidden — plan ended</span> : m.status !== "active" ? <span className="text-amber-700">Awaiting payment</span> : m.expired ? <span className="text-red-600 font-semibold">Expired</span> : <span className="text-emerald-700">Up until {nzDate(m.valid_until)}</span>}</div>
                  </div>
                  <button onClick={async ()=>{
                    try {
                      const { data } = await api.post(`/markdowns/${m.id}/redeem`);
                      toast.success(`Redemption logged · ${data.redemptions} total`);
                      load();
                    } catch (e) { toast.error(formatError(e.response?.data?.detail)); }
                  }} className="text-emerald-700 hover:text-emerald-800 p-2" title="Log an in-store redemption" data-testid={`redeem-${m.id}`}>✓</button>
                  <button onClick={async ()=>{
                    try {
                      const { data } = await api.post(`/markdowns/${m.id}/boost`, { origin_url: window.location.origin });
                      if (data?.checkout_url) { window.location.href = data.checkout_url; return; }
                      toast.success("Boosted for 1 hour ⚡"); load(); loadWallet();
                    } catch (e) { toast.error(formatError(e.response?.data?.detail)); }
                  }} className="text-amber-600 hover:text-amber-700 p-2" title="Boost $1 · pin to top for 1h" data-testid={`boost-${m.id}`}><Zap className="w-4 h-4" /></button>
                  {["active", "paused_plan"].includes(m.status) && <button onClick={()=>renew(m.id)} className="text-emerald-800 hover:text-emerald-900 p-2" title="Renew 30 days · 1 credit" data-testid={`renew-${m.id}`}><RotateCw className="w-4 h-4" /></button>}
                  <button onClick={()=>remove(m.id)} className="text-red-600 hover:text-red-700 p-2" data-testid={`delete-${m.id}`}><Trash2 className="w-4 h-4" /></button>
                </div>
              ))}
            </CardContent>
          </Card>

          <InsightsCard />

          <Card className="border-slate-200" data-testid="followers-card">
            <CardContent className="py-3 flex items-center gap-3">
              <div className="w-9 h-9 rounded-lg bg-amber-100 text-amber-700 grid place-items-center">🔔</div>
              <div className="flex-1 text-sm"><span className="font-display font-bold text-lg" data-testid="follower-count">—</span> shoppers follow you</div>
              <FollowerCountLoader />
            </CardContent>
          </Card>

          <Card className="border-slate-200">
            <CardHeader className="pb-3">
              <CardTitle className="font-display text-xl flex items-center gap-2"><StoreIcon className="w-5 h-5 text-emerald-800" />Shopfront</CardTitle>
              <CardDescription>Upload your logo and shop-front photo — shown on your public store page.</CardDescription>
            </CardHeader>
            <CardContent className="space-y-3">
              <div className="grid grid-cols-2 gap-2">
                <ShopUpload label="Logo" field="logo_path" currentPath={user?.logo_path} onDone={refresh} testid="shop-logo" />
                <ShopUpload label="Shop-front photo" field="shopfront_path" currentPath={user?.shopfront_path} onDone={refresh} testid="shop-shopfront" />
              </div>
              <Input placeholder="Optional tagline (e.g. Fresh, local, whānau-owned)" defaultValue={user?.tagline || ""} onBlur={async (e)=>{
                try { await api.put("/stores/me/shopfront", { tagline: e.target.value }); toast.success("Tagline saved"); refresh(); }
                catch (err) { toast.error(formatError(err.response?.data?.detail)); }
              }} data-testid="shop-tagline" />
              <label className="flex items-center gap-2 text-sm text-slate-700 cursor-pointer pt-1">
                <input
                  type="checkbox"
                  defaultChecked={user?.show_phone !== false}
                  onChange={async (e)=>{
                    try { await api.put("/stores/me/shopfront", { show_phone: e.target.checked }); toast.success(e.target.checked ? "Phone shown to shoppers" : "Phone hidden from shoppers"); refresh(); }
                    catch (err) { toast.error(formatError(err.response?.data?.detail)); }
                  }}
                  data-testid="toggle-show-phone"
                  className="w-4 h-4 accent-emerald-700"
                />
                <Phone className="w-3.5 h-3.5 text-slate-500" />Show my phone number to shoppers
              </label>
              {user?.slug && (
                <Link to={`/qr?store=${user.slug}`} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1.5 text-sm font-semibold text-fuchsia-700 hover:text-fuchsia-800 pt-1" data-testid="shop-poster-link">
                  <Printer className="w-4 h-4" />Download printable QR poster for your window
                </Link>
              )}
            </CardContent>
          </Card>
        </div>
      </div>
      <WalletTopupDialog open={topupOpen} onOpenChange={setTopupOpen} />
    </div>
  );
}
